import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Eye, GitCompare, History, Pencil, RefreshCw, RotateCcw, X } from 'lucide-react';
import { formBuilderApi } from './api';
import FormRenderer from './FormRenderer';
import { FormSchema, FormSummary, VersionItem } from './types';
import { Change, ChangeList } from './components/ChangeList';
import { Chip, timeAgo } from './components/history';
import { primaryButton, secondaryButton, Skeleton } from './ui';
import { ROUTES } from '../../config/links';

const summaryText = (v: VersionItem): string => {
  const s = v.change_summary;
  if (!s) return v.restored_from ? `Same content as v${v.restored_from}` : 'First published version';
  const parts: string[] = [];
  if (s.added) parts.push(`+${s.added} field${s.added === 1 ? '' : 's'}`);
  if (s.removed) parts.push(`−${s.removed} field${s.removed === 1 ? '' : 's'}`);
  if (s.modified) parts.push(`${s.modified} field${s.modified === 1 ? '' : 's'} changed`);
  if (s.other) parts.push(`${s.other} other change${s.other === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'No changes from previous version';
};

export default function FormVersionHistoryPage() {
  const { formId = '' } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number>();
  const [tab, setTab] = useState<'changes' | 'preview'>('changes');
  const [compare, setCompare] = useState<'live' | 'previous'>('live');
  const [changes, setChanges] = useState<Change[]>();
  const [schema, setSchema] = useState<FormSchema>();
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});
  const [form, setForm] = useState<FormSummary>();
  const [formDone, setFormDone] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const published = useMemo(() => versions.filter((v) => v.status === 'published'), [versions]);
  const draft = versions.find((v) => v.status === 'draft');
  const live = published.find((v) => v.is_live);
  const current = versions.find((v) => v.version === selected);
  // A rollback publishes a copy of an older version as a new live version. Follow the
  // restored_from chain so the original version(s) are recognised as "live content".
  const liveSource = useMemo(() => {
    const map = new Map<number, number>();
    const byVersion = new Map(published.map((v) => [v.version, v]));
    let cursor = live;
    while (cursor?.restored_from && !map.has(cursor.restored_from)) {
      map.set(cursor.restored_from, live!.version);
      cursor = byVersion.get(cursor.restored_from);
    }
    return map;
  }, [published, live]);
  const liveCopyOf = (v: VersionItem) => (v.is_live ? undefined : liveSource.get(v.version));

  const load = (keepSelection = false) => {
    setLoading(true);
    return formBuilderApi
      .versions(formId)
      .then((rows) => {
        setVersions(rows);
        setSelected((prev) => (keepSelection && prev && rows.some((r) => r.version === prev) ? prev : (rows.find((r) => r.is_live) || rows[0])?.version));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || e?.message || 'Failed to load versions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [formId]);
  useEffect(() => { setForm(undefined); setFormDone(false); formBuilderApi.get(formId).then(setForm).catch(() => setForm(undefined)).finally(() => setFormDone(true)); }, [formId]);

  useEffect(() => {
    if (selected == null) return;
    setChanges(undefined);
    formBuilderApi.versionDiff(formId, selected, compare).then(setChanges).catch(() => setChanges([]));
  }, [formId, selected, compare]);

  useEffect(() => {
    if (selected == null || tab !== 'preview') return;
    setSchema(undefined);
    setPreviewValues({});
    formBuilderApi.version(formId, selected).then((r) => setSchema(r.schema)).catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load preview'));
  }, [formId, selected, tab]);

  return (
    <div className="flex min-h-full flex-col bg-slate-50 p-3 md:p-8 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <header className="mb-4 flex shrink-0 flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><History className="h-3.5 w-3.5" /> Version history</div>
          {formDone ? <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{form?.name || 'Published versions'}</h1> : <Skeleton className="mt-1 h-7 w-56" />}
          {form?.description && <p className="mt-1 max-w-2xl text-xs text-slate-600">{form.description}</p>}
          <p className="mt-1 max-w-2xl text-xs text-slate-500">Something wrong with the live form? Pick an earlier version, check what differs, and make it live again. Nothing is deleted: the rollback is added as a new version.</p>
        </div>
        <div className="flex gap-2">
          <button className={secondaryButton} onClick={() => load(true)} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.formBuilderAudit(formId))}><History className="h-3.5 w-3.5" /> Audit log</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.FORM_BUILDER)}><ArrowLeft className="h-3.5 w-3.5" /> All forms</button>
        </div>
      </header>

      <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(300px,380px)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
        <ol className="max-h-80 space-y-2 overflow-y-auto pr-1 lg:max-h-none lg:min-h-0" aria-label="Versions">
          {loading && versions.length === 0 && Array.from({ length: 4 }).map((_, i) => <li key={i}><Skeleton className="h-24 w-full rounded-xl" /></li>)}
          {draft && (
            <li className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><span className="text-[13px] font-semibold text-slate-800">Draft (v{draft.version})</span><Chip cls="bg-amber-100 text-amber-700">Unpublished</Chip></div>
                <button className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:underline" onClick={() => navigate(ROUTES.formBuilderEdit(formId))}><Pencil className="h-3 w-3" /> Open editor</button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Work in progress. Rolling back does not touch your draft.</p>
            </li>
          )}
          {published.map((v) => (
            <li key={v.version}>
              <button type="button" onClick={() => setSelected(v.version)} aria-current={selected === v.version}
                className={`w-full rounded-xl border bg-white p-3 text-left shadow-sm transition hover:border-slate-300 ${selected === v.version ? 'border-primary-400 ring-2 ring-primary-100' : 'border-slate-200'}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-slate-900">Version {v.version}</span>
                  {v.is_live ? <Chip cls="bg-emerald-100 text-emerald-700">● Live now</Chip>
                    : liveCopyOf(v) ? <Chip cls="border border-emerald-300 bg-emerald-50 text-emerald-700">● Live as v{liveCopyOf(v)}</Chip>
                    : <Chip cls="bg-slate-100 text-slate-500">Previous</Chip>}
                  {v.restored_from && <Chip cls="bg-violet-100 text-violet-700">Rolled back to v{v.restored_from}</Chip>}
                </div>
                <div className="mt-1 text-[11px] text-slate-500" title={v.published_at ? new Date(v.published_at).toLocaleString() : undefined}>
                  {v.published_at ? `${timeAgo(v.published_at)} · ${new Date(v.published_at).toLocaleDateString()}` : 'Publish date unknown'}
                  {v.published_by_name ? ` · by ${v.published_by_name}` : ''}
                </div>
                {v.note && <p className="mt-1.5 line-clamp-2 text-[12px] italic text-slate-600">“{v.note}”</p>}
                <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
                  <span>{summaryText(v)}</span>
                  <span>{v.step_count} step{v.step_count === 1 ? '' : 's'} · {v.field_count} field{v.field_count === 1 ? '' : 's'}</span>
                </div>
              </button>
            </li>
          ))}
          {!loading && published.length === 0 && <li className="rounded-xl border border-slate-200 bg-white p-6 text-center text-[12px] text-slate-400">This form has not been published yet.</li>}
        </ol>

        <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:min-h-0" aria-label="Version details">
          {!current || current.status !== 'published' ? (
            <div className="grid min-h-[200px] place-items-center p-8 text-[12px] text-slate-400">Select a published version to see what it contains.</div>
          ) : (
            <>
              <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">Version {current.version}{current.is_live ? ' — currently live' : liveCopyOf(current) ? ` — live as v${liveCopyOf(current)}` : ''}</h2>
                  <p className="mt-0.5 text-[11px] text-slate-500">{current.published_by_name ? `Published by ${current.published_by_name}` : 'Published'}{current.published_at ? ` on ${new Date(current.published_at).toLocaleString()}` : ''}</p>
                  {current.note && <p className="mt-1.5 text-[12px] italic text-slate-600">“{current.note}”</p>}
                </div>
                {liveCopyOf(current) ? (
                  <div className="max-w-xs rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                    Already live: v{liveCopyOf(current)} is a rollback copy of this version, so users see this content now.
                    <button type="button" className="ml-1 font-medium underline" onClick={() => setSelected(liveCopyOf(current))}>View v{liveCopyOf(current)}</button>
                  </div>
                ) : !current.is_live && (
                  <button className={primaryButton} onClick={() => setConfirming(true)}><RotateCcw className="h-3.5 w-3.5" /> Make this version live</button>
                )}
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-4 pt-2">
                <div role="tablist" className="flex gap-1">
                  {([['changes', 'What\'s different', GitCompare], ['preview', 'Preview', Eye]] as const).map(([key, label, Icon]) => (
                    <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                      className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[12px] font-medium ${tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                {tab === 'changes' && (
                  <label className="flex items-center gap-1.5 pb-1 text-[11px] text-slate-500">Compare with
                    <select value={compare} onChange={(e) => setCompare(e.target.value as 'live' | 'previous')} className="rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700">
                      <option value="live">Live version{live ? ` (v${live.version})` : ''}</option>
                      <option value="previous">Previous version</option>
                    </select>
                  </label>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {tab === 'changes' && (
                  changes === undefined ? <Skeleton className="h-24 w-full" /> :
                  (current.is_live || liveCopyOf(current)) && compare === 'live' ? <p className="text-[12px] text-slate-500">{current.is_live ? 'This is the live version.' : `This content is already live (as v${liveCopyOf(current)}).`} Compare with the previous version to see what it changed.</p> :
                  changes.length === 0 ? <p className="text-[12px] text-slate-500">No differences.</p> :
                  <>
                    <p className="text-[11px] text-slate-500">{compare === 'live' ? `Changes users would see if v${current.version} replaced the live form:` : `Changes v${current.version} made compared with the previous version:`}</p>
                    <ChangeList changes={changes} />
                  </>
                )}
                {tab === 'preview' && (
                  !schema ? <Skeleton className="h-40 w-full" /> :
                  <div className="rounded-lg border border-slate-200 p-4">
                    <FormRenderer schema={schema} value={previewValues} onChange={setPreviewValues} onSubmit={() => { toast('Preview only: nothing is submitted'); }} runtimeFormId={formId} />
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {confirming && current && live && !liveCopyOf(current) && (
        <RollbackDialog formId={formId} target={current} live={live} nextVersion={Math.max(...versions.map((v) => v.version)) + 1} onClose={() => setConfirming(false)}
          onDone={() => { setConfirming(false); setCompare('previous'); load(); }} />
      )}
    </div>
  );
}

function RollbackDialog({ formId, target, live, nextVersion, onClose, onDone }: { formId: string; target: VersionItem; live: VersionItem; nextVersion: number; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState<Change[]>();
  
  useEffect(() => { formBuilderApi.versionDiff(formId, target.version, 'live').then(setChanges).catch(() => setChanges([])); }, [formId, target.version]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await formBuilderApi.rollback(formId, target.version, reason.trim());
      toast.success(`Version ${target.version} is live again (saved as v${r.version})`);
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Rollback failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-labelledby="rollback-title">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
          <div>
            <h2 id="rollback-title" className="text-base font-semibold text-slate-900">Make version {target.version} the live form?</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">Version {live.version} is live now. People will start seeing version {target.version} straight away.</p>
          </div>
          <button className="rounded-md p-1 text-slate-400 hover:bg-slate-100" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 overflow-y-auto p-4">
          <ul className="space-y-1 rounded-lg bg-slate-50 p-3 text-[12px] text-slate-600">
            <li>• A new version <strong>v{nextVersion}</strong> is created with the same content as v{target.version}.</li>
            <li>• Version {live.version} and all other versions stay in history, so you can switch back.</li>
            <li>• Your current draft is <strong>not</strong> affected.</li>
          </ul>
          <div>
            <div className="text-[12px] font-medium text-slate-700">What will change for users</div>
            {changes === undefined ? <Skeleton className="mt-2 h-16 w-full" /> : changes.length === 0 ? <p className="mt-1 text-[12px] text-slate-500">No visible differences.</p> : <ChangeList changes={changes} />}
          </div>
          <label className="block text-[12px] font-medium text-slate-700">Why are you rolling back? <span className="font-normal text-slate-400">(recorded in history)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={2} placeholder="e.g. Serial number validation rejected valid devices"
              className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-[12px] outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100" />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button className={secondaryButton} onClick={onClose} disabled={busy}>Cancel</button>
          <button className={primaryButton} onClick={confirm} disabled={busy}><RotateCcw className="h-3.5 w-3.5" /> {busy ? 'Rolling back…' : `Roll back to v${target.version}`}</button>
        </div>
      </div>
    </div>
  );
}
